import React, { useEffect, useState } from "react";
import { useLocalStorage } from "react-use";

import {
  Box,
  ExpandableSection,
  FormField,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
} from "@cloudscape-design/components";

import {
  abvMethodOptions,
  calculateAbv,
  sugarScaleOptions,
  toSpecificGravity,
  AbvMethod,
  SugarScale,
} from "../utils/abvCalc";

const AbvCalculator: React.FC = () => {
  const [originalGravity, setOriginalGravity] = useState<string>("1.100");
  const [finalGravity, setFinalGravity] = useState<string>("1.000");
  const [abv, setAbv] = useState<string>("0.00");

  const [expanded, setExpanded] = useLocalStorage("expandedAbv", true);
  const [scale, setScale] = useLocalStorage<SelectProps.Option | null>(
    "abvScale",
    sugarScaleOptions[0]
  );
  const [method, setMethod] = useLocalStorage<SelectProps.Option | null>(
    "abvMethod",
    abvMethodOptions[0]
  );

  const handleCalculate = () => {
    const og = toSpecificGravity(
      parseFloat(originalGravity),
      (scale?.value as SugarScale) || "sg"
    );
    const fg = toSpecificGravity(
      parseFloat(finalGravity),
      (scale?.value as SugarScale) || "sg"
    );

    const calculatedAbv = calculateAbv(
      og,
      fg,
      (method?.value as AbvMethod) || "simple"
    );
    setAbv(calculatedAbv.toFixed(2));
  };

  useEffect(() => {
    if (originalGravity && finalGravity) handleCalculate();
  }, [originalGravity, finalGravity, scale, method]);

  return (
    <ExpandableSection
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerText="ABV Calculator"
    >
      <SpaceBetween size="m">
        <FormField label="Sugar scale">
          <Select
            selectedOption={scale ?? null}
            onChange={({ detail }) => setScale(detail.selectedOption)}
            options={sugarScaleOptions}
          />
        </FormField>
        <FormField label="ABV method">
          <Select
            selectedOption={method ?? null}
            onChange={({ detail }) => setMethod(detail.selectedOption)}
            options={abvMethodOptions}
          />
        </FormField>
        <FormField
          label={`Original gravity (${scale?.label ?? "SG"})`}
        >
          <Input
            value={originalGravity}
            onChange={({ detail }) => setOriginalGravity(detail.value)}
          />
        </FormField>
        <FormField label={`Final gravity (${scale?.label ?? "SG"})`}>
          <Input
            value={finalGravity}
            onChange={({ detail }) => setFinalGravity(detail.value)}
          />
        </FormField>
        <Box variant="h3" fontWeight="heavy">
          <span>ABV: {abv}%</span>
        </Box>
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default AbvCalculator;
